import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';

export const ExcelExportService = {
  /**
   * Genera un archivo Excel con las tareas
   * @param {string} centroCostoId - ID del centro de costo
   * @param {Array} tareas - Tareas transformadas
   * @param {Object} config - Configuración del Excel
   * @returns {Promise<void>}
   */
  async generateExcel(centroCostoId, tareas, config) {
    try {
      if (!tareas || tareas.length === 0) {
        throw new Error('No hay tareas para exportar');
      }

      // Crear headers basados en si es proceso de impresión
      const headers = this.createHeaders(config.isImpresion);
      
      // Formatear tareas para Excel
      const formattedTareas = this.formatTareasForExcel(tareas, config.isImpresion);
      
      // Crear hoja de trabajo
      const ws = XLSX.utils.aoa_to_sheet([headers, ...formattedTareas]);
      
      // Aplicar estilos y formato
      this.applyStyles(ws, config.isImpresion);
      
      // Crear libro de trabajo
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Tareas');
      
      // Generar archivo y descargar
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      saveAs(blob, config.filename);
      
    } catch (error) {
      console.error('Error generating Excel:', error);
      throw error;
    }
  },

  /**
   * Crea los headers para el Excel
   * @param {boolean} isImpresion - Si es proceso de impresión
   * @returns {Array} Headers del Excel
   */
  createHeaders(isImpresion) {
    const baseHeaders = [
      'Número de Orden',
      'Nombre Producto',
      'Listo para empezar',
      'Tiraje-Tamaños',
      'Fecha de entrega'
    ];

    if (isImpresion) {
      return [
        ...baseHeaders,
        'C',
        'M',
        'Y',
        'K',
        'Pantone',
        'Observaciones'
      ];
    }

    return baseHeaders;
  },

  /**
   * Formatea las tareas para el formato Excel
   * @param {Array} tareas - Tareas a formatear
   * @param {boolean} isImpresion - Si es proceso de impresión
   * @returns {Array} Tareas formateadas
   */
  formatTareasForExcel(tareas, isImpresion) {
    return tareas.map(tarea => {
      const baseData = [
        tarea.numero_orden || '',
        tarea.producto || '',
        tarea.listo_para_empezar || '',
        tarea.tiraje_tamanos || '',
        tarea.fecha_entrega || ''
      ];

      if (isImpresion) {
        return [
          ...baseData,
          tarea.c || '',
          tarea.m || '',
          tarea.y || '',
          tarea.k || '',
          tarea.pantone || '',
          tarea.observaciones || ''
        ];
      }

      return baseData;
    });
  },

  /**
   * Aplica estilos a la hoja de trabajo
   * @param {Object} ws - Hoja de trabajo
   * @param {boolean} isImpresion - Si es proceso de impresión
   */
  applyStyles(ws, isImpresion) {
    // Definir ancho de columnas
    const colWidths = isImpresion 
      ? [
          { wch: 15 }, // Número de Orden
          { wch: 30 }, // Nombre Producto
          { wch: 18 }, // Listo para empezar
          { wch: 15 }, // Fecha de entrega
          { wch: 5 },  // C
          { wch: 5 },  // M
          { wch: 5 },  // Y
          { wch: 5 },  // K
          { wch: 15 }  // Pantone
        ]
      : [
          { wch: 15 }, // Número de Orden
          { wch: 30 }, // Nombre Producto
          { wch: 18 }, // Listo para empezar
          { wch: 15 }  // Fecha de entrega
        ];

    ws['!cols'] = colWidths;

    // Aplicar estilos a la primera fila (headers)
    const headerRange = XLSX.utils.decode_range(ws['!ref']);
    for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!ws[cellAddress]) continue;
      
      ws[cellAddress].s = {
        font: { bold: true },
        fill: { fgColor: { rgb: 'E6E6FA' } },
        alignment: { horizontal: 'center', vertical: 'center' }
      };
    }

    // Aplicar color de fondo negro a celdas CMYK que contienen '■'
    if (isImpresion) {
      for (let row = headerRange.s.r + 1; row <= headerRange.e.r; row++) {
        // Columnas C, M, Y, K son las columnas 5, 6, 7, 8 (índices base 0)
        for (let col = 5; col <= 8; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          if (ws[cellAddress] && ws[cellAddress].v === '■') {
            // Inicializar estilo si no existe
            if (!ws[cellAddress].s) ws[cellAddress].s = {};
            
            // Aplicar fondo negro y quitar el texto
            ws[cellAddress].v = '';
            ws[cellAddress].s.fill = { fgColor: { rgb: '000000' } };
            ws[cellAddress].s.alignment = { horizontal: 'center', vertical: 'center' };
          }
        }
      }
    }

  },

  /**
   * Genera el nombre del archivo Excel
   * @param {string} centroCostoNombre - Nombre del centro de costo
   * @param {string} fecha - Fecha en formato YYYY-MM-DD
   * @returns {string} Nombre del archivo
   */
  generateFilename(centroCostoNombre, fecha) {
    const cleanNombre = centroCostoNombre.replace(/[^a-zA-Z0-9]/g, '_');
    return `centro_costo_${cleanNombre}_${fecha}.xlsx`;
  }
};
